import { useContext } from "react";
import { Alert, Button, Form, Row } from "antd";
import { CloudDownloadOutlined, InfoCircleOutlined } from "@ant-design/icons";

import { IGiveaway, IGiveawayHistory, IPanelUser } from "../../../../../interfaces";
import useExportData from "./useExportData";
import FileAndHashContainer from "../../components/FileAndHashContainer";
import { formatDateStringToDateAndTime } from "../../../../../utils/Conversores";
import { ThemeContext } from "../../../../../contexts/ThemeContext";

interface IProps {
    giveaway: IGiveaway | undefined;
    lastGiveawayHistory: IGiveawayHistory | undefined;
    panelUserGeneratedParticipantsFile?: IPanelUser;
    onTicketsFileGenerated: (hash: string, file: string) => void;
}

export default function ExportData({
    giveaway,
    lastGiveawayHistory,
    panelUserGeneratedParticipantsFile,
    onTicketsFileGenerated
}: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const {
        isExportingTickets,
        isDownloadingFile,
        hash,
        file,
        handleClickExportTickets,
        handleClickDownloadFile,
    } = useExportData({ giveaway, lastGiveawayHistory, onTicketsFileGenerated });

    if (!giveaway) {
        return null;
    }

    return (
        <div
            style={{
                background: isDark ? 'rgba(22, 119, 255, 0.04)' : '#f0f7ff',
                border: `1px solid ${isDark ? 'rgba(22, 119, 255, 0.18)' : '#d0e2ff'}`,
                borderRadius: 12,
                padding: '20px 24px',
            }}
        >
            <Form autoComplete="off" layout="vertical">
                {!hash && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <Alert
                            message="Exportação de Bilhetes Concorrentes"
                            description="Nesta etapa, o sistema gera o arquivo criptografado contendo todos os bilhetes elegíveis e seus respectivos participantes para o sorteio. Após gerar o arquivo, utilize-o no software oficial de apuração."
                            type="info"
                            showIcon
                            icon={<InfoCircleOutlined />}
                            style={{
                                borderRadius: 10,
                                background: isDark ? 'rgba(22, 119, 255, 0.08)' : '#ffffff',
                                border: `1px solid ${isDark ? 'rgba(22, 119, 255, 0.22)' : '#bae0ff'}`,
                            }}
                        />

                        <Row justify="center" style={{ marginTop: 8 }}>
                            <Button
                                onClick={handleClickExportTickets}
                                type="primary"
                                icon={<CloudDownloadOutlined />}
                                disabled={isExportingTickets}
                                loading={isExportingTickets}
                                style={{
                                    height: 38,
                                    padding: '0 20px',
                                    borderRadius: 8,
                                    fontWeight: 500,
                                    fontSize: 14,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 8,
                                }}
                            >
                                Gerar Arquivo com os Bilhetes
                            </Button>
                        </Row>
                    </div>
                )}

                {file && hash && (
                    <FileAndHashContainer
                        variant="blue"
                        hash={hash}
                        file={file}
                        downloadFileButtonLabel="Baixar arquivo de bilhetes"
                        fileInfoLabel={
                            panelUserGeneratedParticipantsFile
                                ? `Arquivo gerado em ${formatDateStringToDateAndTime(lastGiveawayHistory?.date_emitted_file_with_participants)} por ${panelUserGeneratedParticipantsFile.name}`
                                : undefined
                        }
                        isDownloadingFile={isDownloadingFile}
                        onClickDownloadFile={handleClickDownloadFile}
                    />
                )}
            </Form>
        </div>
    );
}
